// v3.19-1414 — Operations Command Center (read-only)
//
// One dashboard.  One health model.  Per-worker cards.  Heartbeat,
// last success, last failure, runtime, alerts, drill-down.
//
// Data source: /api/health/workers (SSOT).  Refreshed on demand and
// every 20 seconds.  Any newly-registered worker automatically shows
// up — there are no per-worker views.
import { useEffect, useState, useMemo } from 'react';
import {
  Activity, AlertTriangle, CheckCircle2, XCircle, Clock,
  RefreshCw, Layers, ChevronRight, PauseCircle,
} from 'lucide-react';
import api from '../lib/api';

const REFRESH_MS = 20000;

const STATUS_META = {
  running:  { color: 'emerald', label: 'Running',  Icon: CheckCircle2 },
  starting: { color: 'sky',     label: 'Starting', Icon: Clock },
  stalled:  { color: 'amber',   label: 'Stalled',  Icon: AlertTriangle },
  failed:   { color: 'rose',    label: 'Failed',   Icon: XCircle },
  stopped:  { color: 'slate',   label: 'Stopped',  Icon: PauseCircle },
};

const CATEGORY_LABEL = {
  gmail:         'Gmail',
  telephony:     'Telephony',
  deals:         'Deals & Queue',
  notifications: 'Notifications',
  payments:      'Payments',
  misc:          'Other',
};

const relTime = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const secs = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (secs < 60)  return `${secs}s ago`;
  if (secs < 3600)  return `${Math.floor(secs/60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs/3600)}h ago`;
  return `${Math.floor(secs/86400)}d ago`;
};

const fmtDuration = (s) => {
  if (s == null) return '—';
  if (s < 1)    return `${Math.round(s * 1000)}ms`;
  if (s < 60)   return `${s.toFixed(2)}s`;
  return `${Math.floor(s/60)}m ${Math.round(s%60)}s`;
};

function StatusChip({ status }) {
  const meta = STATUS_META[status] || STATUS_META.stopped;
  const { Icon, label, color } = meta;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-${color}-50 text-${color}-700 border border-${color}-200`}
      data-testid={`worker-status-${status}`}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

function CountsBar({ counts, total }) {
  const items = [
    ['running',  counts.running,  'text-emerald-700 bg-emerald-50 border-emerald-200'],
    ['starting', counts.starting, 'text-sky-700     bg-sky-50     border-sky-200'],
    ['stalled',  counts.stalled,  'text-amber-700   bg-amber-50   border-amber-200'],
    ['failed',   counts.failed,   'text-rose-700    bg-rose-50    border-rose-200'],
    ['stopped',  counts.stopped,  'text-slate-700   bg-slate-100  border-slate-300'],
  ];
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="ops-counts-bar">
      <div className="text-sm font-semibold text-slate-700 mr-1">Total {total}</div>
      {items.map(([k, n, cls]) => (
        <span key={k} className={`px-2.5 py-1 rounded-md border text-xs font-semibold ${cls}`}
          data-testid={`ops-count-${k}`}>
          {k.toUpperCase()} · {n}
        </span>
      ))}
    </div>
  );
}

function WorkerCard({ w, onOpen }) {
  const meta = STATUS_META[w.status] || STATUS_META.stopped;
  const cls = `border-l-4 border-${meta.color}-500`;
  return (
    <button
      type="button"
      onClick={() => onOpen(w)}
      className={`text-left w-full bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-slate-200 ${cls} p-4 space-y-3`}
      data-testid={`worker-card-${w.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            {CATEGORY_LABEL[w.category] || w.category}
          </div>
          <div className="text-sm font-semibold text-slate-900 truncate">
            {w.description}
          </div>
          <div className="text-[11px] font-mono text-slate-500 truncate">{w.key}</div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <StatusChip status={w.status} />
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <div>
          <div className="uppercase tracking-wider text-slate-500 font-semibold">Heartbeat</div>
          <div className="text-slate-800 font-medium">{relTime(w.last_heartbeat_at)}</div>
        </div>
        <div>
          <div className="uppercase tracking-wider text-slate-500 font-semibold">Last Success</div>
          <div className="text-slate-800 font-medium">{relTime(w.last_success_at)}</div>
        </div>
        <div>
          <div className="uppercase tracking-wider text-slate-500 font-semibold">Last Failure</div>
          <div className={`font-medium ${w.last_failure_at ? 'text-rose-700' : 'text-slate-800'}`}>
            {w.last_failure_at ? relTime(w.last_failure_at) : '—'}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-wider text-slate-500 font-semibold">Runtime</div>
          <div className="text-slate-800 font-medium font-mono">
            {fmtDuration(w.last_tick_duration_s)}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-wider text-slate-500 font-semibold">Avg</div>
          <div className="text-slate-800 font-medium font-mono">
            {fmtDuration(w.avg_runtime_s)}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-wider text-slate-500 font-semibold">Queue</div>
          <div className="text-slate-800 font-medium font-mono">
            {w.queue_depth == null ? '—' : w.queue_depth}
          </div>
        </div>
      </div>

      {w.alerts && w.alerts.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`worker-alerts-${w.key}`}>
          {w.alerts.map((a) => (
            <span key={a} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
              {a}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

function DetailDrawer({ worker, onClose }) {
  if (!worker) return null;
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/40 flex items-stretch justify-end"
      onClick={onClose}
      data-testid="worker-detail-drawer"
    >
      <div
        className="w-full max-w-lg bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {CATEGORY_LABEL[worker.category] || worker.category}
            </div>
            <div className="text-lg font-semibold text-slate-900">{worker.description}</div>
            <div className="text-xs font-mono text-slate-500">{worker.key}</div>
          </div>
          <StatusChip status={worker.status} />
        </div>

        <div className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <FactRow k="Worker running"     v={worker.worker_running ? 'yes' : 'no'} />
            <FactRow k="Started"            v={relTime(worker.worker_started_at)} />
            <FactRow k="Interval"           v={worker.interval_seconds ? `${worker.interval_seconds}s` : '—'} />
            <FactRow k="Stall threshold"    v={worker.stall_threshold_s ? `${worker.stall_threshold_s}s` : '—'} />
            <FactRow k="Total ticks"        v={worker.total_ticks} />
            <FactRow k="Total failures"     v={worker.total_failures} />
            <FactRow k="Last heartbeat"     v={relTime(worker.last_heartbeat_at)} />
            <FactRow k="Last success"       v={relTime(worker.last_success_at)} />
            <FactRow k="Last failure"       v={worker.last_failure_at ? relTime(worker.last_failure_at) : '—'} />
            <FactRow k="Last runtime"       v={fmtDuration(worker.last_tick_duration_s)} />
            <FactRow k="Avg runtime"        v={fmtDuration(worker.avg_runtime_s)} />
            <FactRow k="Queue depth"        v={worker.queue_depth == null ? '—' : worker.queue_depth} />
          </div>

          {worker.alerts && worker.alerts.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Alerts</div>
              <div className="flex flex-wrap gap-1.5">
                {worker.alerts.map((a) => (
                  <span key={a} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}

          {worker.last_stats && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Last stats</div>
              <pre className="bg-slate-50 border border-slate-200 rounded-md p-3 text-[11px] font-mono overflow-auto">
                {JSON.stringify(worker.last_stats, null, 2)}
              </pre>
            </div>
          )}

          {worker.last_exception && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold mb-1">Last exception</div>
              <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-[11px] font-mono text-rose-900">
                <div><b>{worker.last_exception.type}</b> — {worker.last_exception.message}</div>
                <div className="text-slate-500 mt-1">at {relTime(worker.last_exception.at)}</div>
                {worker.last_exception.trace && (
                  <pre className="mt-2 whitespace-pre-wrap break-words">{worker.last_exception.trace}</pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FactRow({ k, v }) {
  return (
    <div className="text-xs">
      <div className="uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className="text-slate-800 font-medium font-mono">{v}</div>
    </div>
  );
}

export default function OpsCommandCenterPage() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter]     = useState('all');   // all | running | issues
  const [refreshedAt, setRefreshedAt] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/health/workers');
      setSnapshot(res.data);
      setError(null);
      setRefreshedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load worker health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const groups = useMemo(() => {
    if (!snapshot?.workers) return {};
    const filtered = snapshot.workers.filter((w) => {
      if (filter === 'all') return true;
      if (filter === 'issues') return ['stalled','failed','stopped'].includes(w.status);
      return w.status === filter;
    });
    return filtered.reduce((acc, w) => {
      const cat = w.category || 'misc';
      (acc[cat] = acc[cat] || []).push(w);
      return acc;
    }, {});
  }, [snapshot, filter]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="ops-command-center">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Layers className="h-6 w-6 text-slate-700" />
            <h1 className="text-2xl font-bold text-slate-900">Operations Command Center</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Read-only view of every background worker.  One health model.  One framework.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-slate-500">
            {refreshedAt ? `Refreshed ${relTime(refreshedAt.toISOString())}` : '—'}
          </div>
          <button
            type="button"
            onClick={fetchData}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-sm text-slate-700"
            data-testid="ops-refresh-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {snapshot && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CountsBar counts={snapshot.counts} total={snapshot.total_workers} />
          <div className="flex items-center gap-1 text-xs" data-testid="ops-filter-group">
            {['all','running','issues'].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-md border text-xs font-semibold capitalize ${
                  filter === f
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                }`}
                data-testid={`ops-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-md p-3" data-testid="ops-error">
          <b>Failed to load worker health.</b> {error}
        </div>
      )}

      {!error && snapshot && Object.keys(groups).length === 0 && (
        <div className="bg-slate-50 border border-slate-200 text-slate-600 text-sm rounded-md p-6 text-center">
          No workers match the current filter.
        </div>
      )}

      {Object.entries(groups).map(([category, list]) => (
        <div key={category} className="space-y-3" data-testid={`ops-category-${category}`}>
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-slate-500" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-600">
              {CATEGORY_LABEL[category] || category}
              <span className="ml-2 text-slate-400 font-medium">{list.length}</span>
            </h2>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {list.map((w) => (
              <WorkerCard key={w.key} w={w} onOpen={setSelected} />
            ))}
          </div>
        </div>
      ))}

      <DetailDrawer worker={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
